// frontend/src/lib/journey.ts
// Pure derivation of the six-stage journey (spec §3) + the stage-5 review
// checklist (spec §4). No React, no i18n — labels live in JourneyStrip.
// IN_FLIGHT_STATUSES moved here when PipelineStatusBanner was deleted.

import { ReviewItem, ReviewItemKind } from "./reviewItems";

export const IN_FLIGHT_STATUSES = new Set(["pending", "uploaded", "transcribing", "annotating"]);

export type JourneyStageId = "upload" | "briefing" | "transcription" | "analysis" | "review" | "final";
export type JourneyStageState = "done" | "active" | "needs_you" | "error" | "todo";
export interface JourneyStage { id: JourneyStageId; state: JourneyStageState; }

export type ChecklistItemId = "integrity" | "speakers" | "mishearings" | "names_terms" | "summary";
export interface ChecklistItem {
  id: ChecklistItemId;
  count: number; // 0 for non-count items (summary, integrity)
  // Deep-link destination: "rail" opens/scrolls the review rail; "summary"
  // switches tab; "project_picker" opens the assign control (unassigned
  // proposals); "start_over" opens the guarded Start-over flow (M2 verdict);
  // "regenerate_summary" fires the cheap summary re-run (M2).
  target: "rail" | "summary" | "project_picker" | "start_over" | "regenerate_summary";
}

export interface IntegrityVerdict { verdict: "ok" | "suspect" | "broken"; reason: string; }

export interface JourneyMeeting {
  status: string;
  locked_at: string | null;
  context: unknown | null;
}

export interface JourneyInputs {
  hasTranscript: boolean;
  hasSummary: boolean;
  summaryRead: boolean;
  openProposalCount: number; // displayAttendees.length + displayTerms.length
  hasProject: boolean;
  reviewItems: ReviewItem[];
  integrity: IntegrityVerdict | null; // M2; null in M1
}

export interface Journey {
  stages: JourneyStage[]; // always 6, ordered
  currentIdx: number;     // index of the active/needs_you/error stage; 5 when Final
  errorStage: JourneyStageId | null;
  checklist: ChecklistItem[];
  pendingCount: number;   // count chips by count, zero-count chips as 1 each
  integrity: IntegrityVerdict | null; // passthrough for the strip's suspect note
}

// Spec §4 derives "speaker suggestions" from "pending claude_speaker items",
// but the current pipeline emits speaker doubts as attribution_second_look /
// role_inversion flags (claude_speaker items are legacy L3 only). Both are
// included so the chip reflects what the rail actually shows.
export const SPEAKER_SUGGESTION_KINDS: Set<ReviewItemKind> = new Set([
  "attribution_second_look", "role_inversion", "claude_speaker_proposal", "claude_speaker_uncertain",
]);
export const MISHEARING_KINDS: Set<ReviewItemKind> = new Set([
  "proper_noun_oov", "lost_thread", "silence_fill", "code_switch_break",
  "loop_repetition", "out_of_context_passage",
]);

const ORDER: JourneyStageId[] = ["upload", "briefing", "transcription", "analysis", "review", "final"];

function stagesUpTo(currentIdx: number, currentState: JourneyStageState): JourneyStage[] {
  return ORDER.map((id, i) => ({
    id,
    state: i < currentIdx ? "done" : i === currentIdx ? currentState : "todo",
  }));
}

export function deriveJourney(meeting: JourneyMeeting, inputs: JourneyInputs): Journey {
  const empty: Journey = {
    stages: [], currentIdx: 0, errorStage: null, checklist: [], pendingCount: 0,
    integrity: inputs.integrity ?? null,
  };

  // Final wins over everything (a locked meeting can keep status 'ready').
  if (meeting.locked_at) {
    return { ...empty, stages: ORDER.map((id) => ({ id, state: "done" as const })), currentIdx: 5 };
  }

  if (meeting.status === "error") {
    // Spec §3 error attribution: context null → Briefing; context + no
    // transcript → Transcription; transcript present → Analysis.
    const errorStage: JourneyStageId =
      !meeting.context ? "briefing" : !inputs.hasTranscript ? "transcription" : "analysis";
    const idx = ORDER.indexOf(errorStage);
    return { ...empty, stages: stagesUpTo(idx, "error"), currentIdx: idx, errorStage };
  }

  if (meeting.status === "pending" || meeting.status === "uploaded") {
    return { ...empty, stages: stagesUpTo(1, "active"), currentIdx: 1 };
  }
  if (meeting.status === "awaiting_context_review") {
    return { ...empty, stages: stagesUpTo(1, "needs_you"), currentIdx: 1 };
  }
  if (meeting.status === "transcribing") {
    return { ...empty, stages: stagesUpTo(2, "active"), currentIdx: 2 };
  }
  if (meeting.status === "annotating") {
    return { ...empty, stages: stagesUpTo(3, "active"), currentIdx: 3 };
  }

  // status === "ready", unlocked → stage 5 with the derived checklist.
  const pendingItems = inputs.reviewItems.filter((i) => i.status === "pending");
  const speakers = pendingItems.filter((i) => SPEAKER_SUGGESTION_KINDS.has(i.kind)).length;
  const mishearings = pendingItems.filter((i) => MISHEARING_KINDS.has(i.kind)).length;

  const checklist: ChecklistItem[] = [];
  // M2: integrity verdict is the distinguished FIRST item when broken.
  if (inputs.integrity?.verdict === "broken") {
    checklist.push({ id: "integrity", count: 0, target: "start_over" });
  }
  if (speakers > 0) checklist.push({ id: "speakers", count: speakers, target: "rail" });
  if (mishearings > 0) checklist.push({ id: "mishearings", count: mishearings, target: "rail" });
  if (inputs.openProposalCount > 0) {
    checklist.push({
      id: "names_terms", count: inputs.openProposalCount,
      target: inputs.hasProject ? "rail" : "project_picker",
    });
  }
  if (inputs.hasSummary) {
    if (!inputs.summaryRead) checklist.push({ id: "summary", count: 0, target: "summary" });
  } else if (inputs.hasTranscript) {
    // M2: missing/failed summary → the chip becomes the regenerate offer.
    checklist.push({ id: "summary", count: 0, target: "regenerate_summary" });
  }

  const pendingCount = checklist.reduce((acc, c) => acc + (c.count > 0 ? c.count : 1), 0);
  return {
    stages: stagesUpTo(4, "active"), currentIdx: 4, errorStage: null,
    checklist, pendingCount, integrity: inputs.integrity ?? null,
  };
}

// --- Summary-read marker (same localStorage pattern as recentlyAdded.ts) ----
const SUMMARY_READ_PREFIX = "timlul:meeting-summary-read:";

export function readSummaryRead(meetingId: string): boolean {
  try { return localStorage.getItem(SUMMARY_READ_PREFIX + meetingId) !== null; }
  catch { return false; }
}
export function writeSummaryRead(meetingId: string): void {
  try { localStorage.setItem(SUMMARY_READ_PREFIX + meetingId, new Date().toISOString()); }
  catch { /* storage unavailable — marker degrades to always-unread */ }
}
